/*********************
 * Intervention Selection Formatter
 *
 * Formats an intervention action chosen by a policy
 * with all additional fields needed for lexa app consumption
 *********************/
import { interventionSituationTypes, interventionTypes } from "./constants";
import { versions } from ".";

export default function formatAction(action, state, internalState) {
  // Format action for output, depending on whether it's a phrase or word level intervention, or a "word prep" action
  if (
    state.interactions.scope.toUpperCase() == interventionSituationTypes.WORD
  ) {
    return formatWordLevelAction(action, state, internalState);
  }

  // Handle word prep
  if (
    state.interactions.scope.toUpperCase() ==
    interventionSituationTypes.WORD_PREP
  ) {
    return formatWordPrepAction(action, state);
  }

  // Handle phrase level
  let formattedAction = formatPhraseLevelAction(action, state, internalState);

  // If this is a tense intervention, return specific tenseError as closeError (even if undefined)
  if (action.intervention == interventionTypes.tense) {
    formattedAction.closeError = action.metadata.tenseError;
  }

  formattedAction.versions = versions;
  return formattedAction;
}

// WORD level actions currently require the following fields:
/*
  type: chosenIntervention,
  metadata,
  word,
*/
function formatWordLevelAction(action, state, internalState) {
  return {
    log: action.log,
    decisionContextAcrossPasses: action.decisionContextAcrossPasses,
    type: action.intervention,
    fallback: action.fallbackIntervention,
    passSelected: action.passSelected,
    word: state.interactions.inWord,
    index: action.metadata.wordIndex,
    metadata: getWordMetadata(state.interactions.inWord, state),
    history: internalState.getInterventionHistoryByPhrase(),
    rankedInterventionTracking:
      action.metadata.rankedInterventionSelector.interventionTracking,
    phraseInterventionWeights: action.metadata.phraseInterventionWeights
      ? action.metadata.phraseInterventionWeights
      : [],
  };
}

// WORD PREP actions currently require the following fields:
/*
  pauseLength
*/
function formatWordPrepAction(action, state) {
  return {
    pauseLength: action.pauseLength,
    decisionContext: action.decisionContext,
    word: state.interactions.inWord,
    index: action.metadata.wordIndex,
    type: interventionSituationTypes.WORD_PREP,
    rankedInterventionTracking:
      action.metadata.rankedInterventionSelector.interventionTracking,
    phraseInterventionWeights: action.metadata.phraseInterventionWeights
      ? action.metadata.phraseInterventionWeights
      : [],
  };
}

// PHRASE level actions currently require the following fields:
/*
  type: chosenIntervention,
  word: chosenError.word,
  index: chosenError.index,
  scope: scope,
  metadata,
  errorsInLastPhrase,
  closeError,
*/
function formatPhraseLevelAction(action, state, internalState) {
  return {
    log: action.log,
    decisionContextAcrossPasses: action.decisionContextAcrossPasses,
    type: action.intervention,
    fallback: action.fallbackIntervention,
    passSelected: action.passSelected,
    word: action.metadata.word,
    index: action.metadata.wordIndex,
    metadata: getWordMetadata(action.metadata.word, state),
    errorsInLastPhrase: action.metadata.errorsInPhrase,
    mostRecentAccuracy: action.metadata.mostRecentAccuracy,
    closeError: action.metadata.closeError,
    frustration: action.metadata.scores.frustration,
    scores: action.metadata.scores,
    history: internalState.getInterventionHistoryByPhrase(),
    rankedInterventionTracking:
      action.metadata.rankedInterventionSelector.interventionTracking,
    phraseInterventionWeights: action.metadata.phraseInterventionWeights
      ? action.metadata.phraseInterventionWeights
      : [],
  };
}

function getWordMetadata(chosenWord, state) {
  // if we didn't intervene on a word, there's no corresponding metadata
  if (!chosenWord) {
    return null;
  }
  // search through the phrase metadata for the right word
  let words = state.story.textMetadataForCurrentPhrase.words;
  return words.find((word) => {
    if (!word || !word.WORD) {
      return false;
    }
    return (
      word.WORD.replace("'", "").replace(",", "") ==
      chosenWord.toUpperCase().replace("'", "").replace(",", "")
    );
  });
}
